import { Injectable } from '@angular/core';
import { RouterStateSnapshot } from '@angular/router';
import { CustomActivatedRouteSnapshot, CustomPageType } from '@core/const';
import { RouterStateSerializer } from '@ngrx/router-store';
import {
  ActivatedRouterStateSnapshot,
  HOME_PAGE_CONTEXT,
  PageContext,
  PageType,
  RoutingConfigService,
  SMART_EDIT_CONTEXT,
} from '@spartacus/core';

// Spartacus update warning: Был скопирован CustomSerializer
/* The serializer is there to parse the RouterStateSnapshot,
and to reduce the amount of properties to be passed to the reducer.
 */
@Injectable()
export class CustomRouterStateSerializer implements RouterStateSerializer<ActivatedRouterStateSnapshot> {
  constructor(private routingConfig: RoutingConfigService) {}

  serialize(routerState: RouterStateSnapshot): ActivatedRouterStateSnapshot {
    let state = routerState.root as CustomActivatedRouteSnapshot;
    let cmsRequired = false;
    let context: PageContext;
    let semanticRoute: string;
    let urlString = '';

    while (state.firstChild) {
      state = state.firstChild as CustomActivatedRouteSnapshot;
      urlString += '/' + state.url.map((urlSegment) => urlSegment.path).join('/');

      // we use semantic route information embedded from any parent route
      if (state.data?.cxRoute || state.data?.semanticName) {
        semanticRoute = state.data?.cxRoute || state.data?.semanticName;
      }

      // Используем контекст из роутов, в приоритете кастомные
      if (state.data && (state.data.customCmsRouteContext || state.data.cxCmsRouteContext)) {
        context = (state.data.customCmsRouteContext || state.data.cxCmsRouteContext) as PageContext;
      }

      // we assume, that any route that has CmsPageGuard or it's child
      // is cmsRequired
      if (
        !cmsRequired &&
        (context ||
          (state.routeConfig &&
            state.routeConfig.canActivate &&
            state.routeConfig.canActivate.find((x) => x && x.guardName === 'CmsPageGuard')))
      ) {
        cmsRequired = true;
      }
    }

    // If `semanticRoute` couldn't be already recognized using `data.cxRoute` property
    // let's lookup the routing configuration to find the semantic route that has exactly the same configured path as the current URL.
    // This will work only for simple URLs without any dynamic routing parameters.
    semanticRoute = semanticRoute || this.lookupSemanticRoute(urlString);

    const { params } = state;
    // we give smartedit preview page a PageContext
    if (state.url.length > 0 && state.url[0].path === 'cx-preview') {
      context = {
        id: SMART_EDIT_CONTEXT,
        type: PageType.CONTENT_PAGE,
      };
    } else {
      if (<PageType | CustomPageType>context?.type === CustomPageType.TagPage && params['tagSlug']) {
        context = { ...context, id: params['tagSlug'] };
      } else if (context?.type === PageType.CATEGORY_PAGE && !params['categoryCode']) {
        context = { ...context, id: 'catalog' };
      } else if (context?.type && context.type !== PageType.CONTENT_PAGE && params['promoCode']) {
        context = { ...context, id: params['promoCode'] };
      } else if (params['productCode']) {
        context = { id: params['productCode'], type: PageType.PRODUCT_PAGE };
      } else if (params['categoryCode'] && state.data?.cxRoute === 'category') {
        context = { id: params['categoryCode'], type: PageType.CATEGORY_PAGE };
      } else if (params['brandCode']) {
        context = { id: params['brandCode'], type: PageType.CATEGORY_PAGE };
      } else if (state.data.pageLabel !== undefined) {
        context = { id: state.data.pageLabel, type: PageType.CONTENT_PAGE };
      } else if (context?.type && !context.id && params['code']) {
        context = { ...context, id: params['code'] };
      } else if (!context) {
        if (state.url.length > 0) {
          const pageLabel = '/' + state.url.map((urlSegment) => urlSegment.path).join('/');
          context = {
            id: pageLabel,
            type: PageType.CONTENT_PAGE,
          };
        } else {
          context = {
            // We like URLs to be driven by the backend, the CMS actually returns the homepage
            // if no page label is given. Our logic however requires an id. undefined doesn't work.
            id: HOME_PAGE_CONTEXT,

            // We currently need to support a hardcoded page type, since the internal store uses the page
            // type to store the content.
            type: PageType.CONTENT_PAGE,
          };
        }
      }
    }

    const data: ActivatedRouterStateSnapshot = {
      url: routerState.url,
      queryParams: routerState.root.queryParams,
      params,
      context,
      cmsRequired,
      semanticRoute,
    };

    return data;
  }

  /**
   * Returns the semantic route name for given page label.
   *
   * *NOTE*: It works only for simple static urls that are equal to the page label
   * of cms-driven content page. For example: `/my-account/address-book`.
   *
   * It doesn't work for URLs with dynamic parameters. But such case can be handled
   * by reading the defined `data.cxRoute` from the Angular Routes.
   *
   * @param path path to be found in the routing config
   */
  private lookupSemanticRoute(path: string): string {
    // Page label is assumed to start with `/`, but Spartacus configured paths
    // don't start with slash. So we remove the leading slash:
    return this.routingConfig.getRouteName(path.substring(1));
  }
}
